import { useEffect, useRef, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";

// Marketing pages
import MarketingHome from "./pages/MarketingHome";
import Features from "./pages/Features";
import About from "./pages/About";
import Pricing from "./pages/Pricing";

// App pages
import Auth from "./pages/Auth";
import Garden from "./pages/Garden";
import Dashboard from "./pages/Dashboard";
import Plan from "./pages/Plan";
import Grove from "./pages/Grove";
import Withered from "./pages/Withered";
import Archived from "./pages/Archived";
import Profile from "./pages/Profile";
import { 
  GrowthInstrumentsHub, 
  CompetitorAnalyzer, 
  ProductFinder 
} from "./pages/growth-instruments";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children?: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [forceReady, setForceReady] = useState(false);
  const didToast = useRef(false);

  useEffect(() => {
    if (!loading) {
      setForceReady(false);
      didToast.current = false;
      return;
    }

    const t = window.setTimeout(() => {
      setForceReady(true);
      if (!didToast.current) {
        didToast.current = true;
        toast.message('Syncing profile…');
      }
    }, 3000);

    return () => window.clearTimeout(t);
  }, [loading]);

  if (loading && !forceReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-primary animate-pulse">YELV</h1>
        </div>
      </div>
    );
  }

  if (!user && (!loading || forceReady)) {
    return <Navigate to="/login" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return null;
  }
  
  if (user) {
    return <Navigate to="/app" replace />;
  }
  
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing pages */}
      <Route path="/" element={<MarketingHome />} />
      <Route path="/features" element={<Features />} />
      <Route path="/about" element={<About />} />
      <Route path="/pricing" element={<Pricing />} />
      
      {/* Auth routes */}
      <Route path="/login" element={<AuthRoute><Auth /></AuthRoute>} />
      <Route path="/auth" element={<Navigate to="/login" replace />} />
      
      {/* Legacy redirects for old paths - backwards compatibility */}
      <Route path="/growth-instruments" element={<Navigate to="/app/growth-instruments" replace />} />
      <Route path="/growth-instruments/competitor-analyzer" element={<Navigate to="/app/growth-instruments/competitor-analyzer" replace />} />
      <Route path="/growth-instruments/product-finder" element={<Navigate to="/app/growth-instruments/product-finder" replace />} />
      <Route path="/growth-instruments/coi" element={<Navigate to="/app/growth-instruments/competitor-analyzer" replace />} />
      <Route path="/growth-instruments/*" element={<Navigate to="/app/growth-instruments" replace />} />
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/plan" element={<Navigate to="/app/plan" replace />} />
      <Route path="/plan/:ideaId" element={<Navigate to="/app/plan/:ideaId" replace />} />
      <Route path="/profile" element={<Navigate to="/app/profile" replace />} />
      <Route path="/grove" element={<Navigate to="/app/grove" replace />} />
      <Route path="/withered" element={<Navigate to="/app/withered" replace />} />
      <Route path="/archived" element={<Navigate to="/app/archived" replace />} />
      
      {/* Protected app routes with sidebar layout */}
      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Garden />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="plan" element={<Plan />} />
        <Route path="plan/:ideaId" element={<Plan />} />
        <Route path="grove" element={<Grove />} />
        <Route path="withered" element={<Withered />} />
        <Route path="archived" element={<Archived />} />
        <Route path="profile" element={<Profile />} />
        <Route path="growth-instruments" element={<GrowthInstrumentsHub />} />
        <Route path="growth-instruments/competitor-analyzer" element={<CompetitorAnalyzer />} />
        <Route path="growth-instruments/product-finder" element={<ProductFinder />} />
      </Route>
      
      {/* Placeholder routes for footer links */}
      <Route path="/privacy" element={<NotFound />} />
      <Route path="/terms" element={<NotFound />} />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
